import { useEffect, useState } from "react";
import api from "../lib/api";
import { Heart, TrendingUp, TrendingDown, MessageSquare, AlertTriangle } from "lucide-react";

/**
 * Manager view of the weekly Staff Pulse.
 *
 * Shows salon-wide average mood trend, participation rate, and a per-staff
 * list sorted lowest → highest (so struggling teammates surface first).
 * Clicking a row expands to reveal their trend bars + latest note.
 */
export default function StaffPulseSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // user_id or null

  useEffect(() => {
    let mounted = true;
    api.get("/staff-pulse/dashboard?weeks=8")
      .then(res => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setData({ error: true }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8">
        <div className="h-6 w-40 bg-slate-100 rounded animate-pulse" />
      </div>
    );
  }
  if (!data || data.error) return null;

  const { weekly_average = [], staff = [], participation = {}, mood_emoji = {}, mood_labels = {} } = data;
  const latestAvg = [...weekly_average].reverse().find(w => w.average !== null)?.average;
  const priorAvg = weekly_average
    .slice(0, -1)
    .filter(w => w.average !== null)
    .slice(-1)[0]?.average;
  const delta = latestAvg != null && priorAvg != null ? latestAvg - priorAvg : null;

  const needsAttention = staff.filter(s => s.latest_mood != null && s.latest_mood <= 2);

  return (
    <div data-testid="staff-pulse-section" className="bg-white border border-slate-200 rounded-2xl p-6 mb-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-rose-400" strokeWidth={1.5} />
          <div>
            <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Team Pulse</h3>
            <p className="text-xs text-slate-400 font-['Manrope']">Weekly 1-question mood check-in</p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-right">
            <div className="text-slate-400 uppercase tracking-widest font-['Manrope'] text-[10px] font-bold">This Week</div>
            <div className="text-slate-900 font-medium">
              {participation.responded || 0} / {participation.total_staff || 0}
              <span className="text-slate-400 ml-1">
                ({Math.round((participation.rate || 0) * 100)}%)
              </span>
            </div>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div className="text-right">
            <div className="text-slate-400 uppercase tracking-widest font-['Manrope'] text-[10px] font-bold">Avg Mood</div>
            <div className="flex items-baseline gap-1 justify-end">
              <span className="font-['Outfit'] text-xl text-slate-900">
                {latestAvg != null ? latestAvg.toFixed(1) : "—"}
              </span>
              {delta != null && delta !== 0 && (
                <span className={`flex items-center text-[11px] font-medium ${delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(delta).toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Salon-wide weekly trend (tiny bar chart) */}
      <div className="grid grid-cols-8 gap-1.5 mb-5">
        {weekly_average.map(w => {
          const h = w.average ? Math.max(8, (w.average / 5) * 60) : 4;
          const color = !w.average ? "bg-slate-100"
            : w.average >= 4 ? "bg-emerald-400"
            : w.average >= 3 ? "bg-amber-400" : "bg-rose-400";
          return (
            <div key={w.week_of} className="flex flex-col items-center gap-1">
              <div className="h-[60px] w-full flex items-end">
                <div className={`${color} w-full rounded-t transition-all`} style={{ height: `${h}px` }} />
              </div>
              <span className="text-[9px] text-slate-400 font-['Manrope']">{w.week_of.split("-W")[1]}</span>
            </div>
          );
        })}
      </div>

      {/* Needs attention callout */}
      {needsAttention.length > 0 && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 flex items-center gap-2" data-testid="pulse-needs-attention">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
          <span className="text-xs text-rose-700 font-medium">
            {needsAttention.length} teammate{needsAttention.length > 1 ? "s" : ""} flagged a rough week
          </span>
        </div>
      )}

      {/* Per-staff list */}
      {staff.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-slate-400 font-['Manrope']">No pulse responses yet — prompts appear in staff portals on Fridays.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {staff.map(s => {
            const isOpen = expanded === s.user_id;
            const moodEmoji = s.latest_mood ? mood_emoji[s.latest_mood] : "·";
            const moodLabel = s.latest_mood ? mood_labels[s.latest_mood] : "No response yet";
            return (
              <div key={s.user_id}>
                <button
                  onClick={() => setExpanded(isOpen ? null : s.user_id)}
                  data-testid={`pulse-staff-${s.user_id}`}
                  className="w-full flex items-center gap-3 py-3 hover:bg-[#FAFAFA] px-2 rounded-lg text-left transition-colors"
                >
                  <span className="text-xl w-8 text-center">{moodEmoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{s.user_name}</p>
                    <p className="text-[11px] text-slate-400 font-['Manrope']">
                      {moodLabel}
                      {s.total_responses > 0 && ` · avg ${s.average}`}
                      {s.total_responses > 0 && ` · ${s.total_responses}/8 weeks`}
                    </p>
                  </div>
                  <div className="flex items-end gap-[2px] h-6" aria-hidden>
                    {(s.trend || []).map((m, i) => {
                      const h = m ? Math.max(3, (m / 5) * 22) : 2;
                      const color = !m ? "bg-slate-100"
                        : m >= 4 ? "bg-emerald-400"
                        : m >= 3 ? "bg-amber-400" : "bg-rose-400";
                      return <div key={i} className={`${color} w-1 rounded-t`} style={{ height: `${h}px` }} />;
                    })}
                  </div>
                  {s.latest_note && (
                    <MessageSquare className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                  )}
                </button>
                {isOpen && s.latest_note && (
                  <div className="ml-11 mb-3 -mt-1 text-xs text-slate-600 bg-[#FAFAFA] rounded-lg p-3 italic font-['Manrope']" data-testid={`pulse-note-${s.user_id}`}>
                    &ldquo;{s.latest_note}&rdquo;
                    <div className="text-[10px] text-slate-400 mt-1 not-italic">Week {s.latest_week}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
